import { getConfig } from "@/features/branding";
import type { AnalysisResultsTab, AnalysisView } from "@/features/maps/types";
import Router, { useRouter } from "next/router";
import { useCallback, useEffect } from "react";

const ANALYSIS_VIEW_PATHS = {
  portfolio: "aggregated",
  point: "points",
} as const satisfies Record<AnalysisView, string>;

const RESULTS_TABS = {
  risk: "risk",
  exposure: "flood-exposure",
  adaptation: "resilience",
} as const satisfies Record<AnalysisResultsTab, string>;

const DEFAULT_RESULTS_TAB: AnalysisResultsTab = "risk";

interface ParsedRoute {
  /** The view the URL resolves to; unrecognised paths fall back to the default. */
  view: AnalysisView;
  /** The selected point: `/portfolios/:id/points/:pointId`. */
  pointId: string | null;
  /** False when the URL says something other than what it resolved to, so needs normalising. */
  isCanonical: boolean;
}

export interface UseAnalysisViewRouteParams {
  id: string | undefined;
  isLargeSite: boolean;
  canRunAutoAdaptation?: boolean;
}

/** The slice of collection state that lives in the URL */
export interface AnalysisRouteState {
  tab: AnalysisView;
  setTab: (tab: AnalysisView) => void;
  activeTab: AnalysisResultsTab;
  setActiveTab: (activeTab: AnalysisResultsTab) => void;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  /** Open a point's detail in the point-level view, from any view, in one navigation. */
  openPointDetail: (pointId: string) => void;
}

/** Reads the `[[...view]]` catch-all segment of a collection route. */
const parseRoute = (segment: string | string[] | undefined, aggregatedEnabled: boolean): ParsedRoute => {
  const segments = Array.isArray(segment) ? segment : segment ? [segment] : [];
  const [viewPath, pointPath, ...rest] = segments;

  const views = Object.keys(ANALYSIS_VIEW_PATHS) as AnalysisView[];
  const requested = views.find((view) => ANALYSIS_VIEW_PATHS[view] === viewPath);
  const isAvailable = requested === "point" || aggregatedEnabled;

  const view = requested && isAvailable ? requested : aggregatedEnabled ? "portfolio" : "point";
  // Only the point-level view has a detail view
  const pointId = requested === "point" ? pointPath || null : null;

  const isCanonical = view === requested && pointPath === (pointId ?? undefined) && !rest.length;

  return { view, pointId, isCanonical };
};

const resultsTabFromQuery = (value: string | string[] | undefined): AnalysisResultsTab | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  const tabs = Object.keys(RESULTS_TABS) as AnalysisResultsTab[];

  return tabs.find((tab) => RESULTS_TABS[tab] === raw) ?? null;
};

/** Canonical URL for one view of one collection */
const collectionViewHref = (isLargeSite: boolean, id: string, view: AnalysisView, pointId?: string | null): string => {
  const collectionTypePath = isLargeSite ? "/large-sites" : "/portfolios";
  const base = `${collectionTypePath}/${id}/${ANALYSIS_VIEW_PATHS[view]}`;

  return pointId ? `${base}/${encodeURIComponent(pointId)}` : base;
};

/**
 * Binds the aggregated and point-level view, the selected point and the
 * point-level results tab to the URL.
 */
export const useAnalysisViewRoute = ({
  id,
  isLargeSite,
  canRunAutoAdaptation = false,
}: UseAnalysisViewRouteParams): AnalysisRouteState => {
  const { query, isReady } = useRouter();

  const aggregatedEnabled = getConfig().portfolioExport.visualization?.portfolioLevel?.enabled ?? false;
  const { view: tab, pointId: selectedId, isCanonical } = parseRoute(query.view, aggregatedEnabled);
  const requestedTab = resultsTabFromQuery(query.tab) ?? DEFAULT_RESULTS_TAB;
  const activeTab = requestedTab === "adaptation" && !canRunAutoAdaptation ? DEFAULT_RESULTS_TAB : requestedTab;

  useEffect(() => {
    if (!isReady || !id || isCanonical) return;

    Router.replace(collectionViewHref(isLargeSite, id, tab, selectedId), undefined, { shallow: true });
  }, [isReady, id, isLargeSite, tab, selectedId, isCanonical]);

  const setTab = useCallback(
    (next: AnalysisView) => {
      if (!id) return;

      Router.push(collectionViewHref(isLargeSite, id, next), undefined, { shallow: true });
    },
    [id, isLargeSite]
  );

  const setSelectedId = useCallback(
    (next: string | null) => {
      const viewPath = ANALYSIS_VIEW_PATHS[parseRoute(Router.query.view, aggregatedEnabled).view];
      const query = { ...Router.query, view: next ? [viewPath, next] : [viewPath] };

      Router.push({ pathname: Router.pathname, query }, undefined, { shallow: true });
    },
    [aggregatedEnabled]
  );

  const openPointDetail = useCallback(
    (pointId: string) => {
      if (!id) return;
      Router.push(collectionViewHref(isLargeSite, id, "point", pointId), undefined, { shallow: true });
    },
    [id, isLargeSite]
  );

  const setActiveTab = useCallback((next: AnalysisResultsTab) => {
    const query = { ...Router.query };

    if (next === DEFAULT_RESULTS_TAB) delete query.tab;
    else query.tab = RESULTS_TABS[next];
    Router.replace({ pathname: Router.pathname, query }, undefined, { shallow: true });
  }, []);

  // Drop a tab the URL asked for but the permission refused, so the address bar
  // stops advertising a view the user cannot open.
  useEffect(() => {
    if (!isReady || activeTab === requestedTab) return;

    setActiveTab(activeTab);
  }, [isReady, activeTab, requestedTab, setActiveTab]);

  return { tab, setTab, activeTab, setActiveTab, selectedId, setSelectedId, openPointDetail };
};
